"use client";

import { useRouter } from "next/navigation";
import { useState } from "react";
import type { ReportPurchaseResult } from "@otalytics/shared";

import { api } from "@/lib/api/client";
import { readCheckoutContext } from "@/lib/analytics/checkout-context";

/**
 * Buys one city, from either side of the paywall.
 *
 * One endpoint serves both callers because only the server can tell them apart:
 * `/destinations/[slug]` is statically rendered with `revalidate: 300`, so the
 * page cannot know whether the reader is signed in. `api` attaches a bearer
 * token when a session exists and omits it when one does not, and the API reads
 * that to decide between adding a line to an existing subscription and opening a
 * Checkout session that will mint the buyer an account.
 *
 * The client never guesses which happened — it does what the response says.
 */
export function useReportPurchase(slug: string): {
  purchase: () => Promise<void>;
  isSubmitting: boolean;
  error: string | null;
} {
  const router = useRouter();
  const [isSubmitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const purchase = async (): Promise<void> => {
    // City checkout is the monthly Price. The client does not send an
    // interval or a subscription kind; the webhook reads the interval from
    // the Stripe Price. Attribution is what this body is for.
    setError(null);
    setSubmitting(true);

    try {
      const result = await api.post<ReportPurchaseResult>(
        `/api/public-reports/${encodeURIComponent(slug)}/checkout`,
        readCheckoutContext(slug)
      );

      if (result.mode === "checkout") {
        window.location.assign(result.url);
        // Deliberately still submitting: the tab is navigating to Stripe, and
        // re-enabling the button invites a second click that starts a second
        // session against the same card.
        return;
      }

      router.push(`/reports/${slug}`);
      router.refresh();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not start the purchase."
      );
      setSubmitting(false);
    }
  };

  return { purchase, isSubmitting, error };
}
